import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { CreateDatabaseResponse, Database, Project } from '@tallypg/contracts';
import { api, formatBytes, formatDate, formatMoney, useApi, type Forecast } from '../api';
import { CredentialsReveal, Modal, StatusBadge } from '../components';

type SizeHint = 'tiny' | 'small' | 'medium' | 'large';
const SIZES: { id: SizeHint; label: string; hint: string }[] = [
  { id: 'tiny', label: 'Tiny', hint: 'Under 100 MB. Side projects, prototypes, previews.' },
  { id: 'small', label: 'Small', hint: '100 MB to 2 GB. Typical app or SaaS backend.' },
  { id: 'medium', label: 'Medium', hint: '2 to 20 GB. Busy app, analytics, many users.' },
  { id: 'large', label: 'Large', hint: 'Over 20 GB or heavy writes. Tell us and we plan capacity.' },
];

/**
 * Three-step wizard: name & project -> location -> expected size -> create. The size is only a planning
 * hint (nothing is capped by it) and the location list comes from the regions that are live right now.
 */
/**
 * Latency from this browser to each live region's gateway host: three quick HTTPS fetches per region
 * (opaque, no CORS needed; Caddy answers on 443 with a short text), lowest sample wins. Unreachable hosts
 * (local dev, blocked networks) are simply skipped and the geolocation distance is used instead.
 */
function useRegionPing(regions: { id: string; gatewayHost: string }[]) {
  const key = regions.map((r) => `${r.id}@${r.gatewayHost}`).join(',');
  const [state, setState] = useState<{ ms: Record<string, number>; best: string | null; pending: boolean }>({
    ms: {},
    best: null,
    pending: false,
  });
  useEffect(() => {
    if (!regions.length) return;
    let alive = true;
    setState({ ms: {}, best: null, pending: true });
    const measure = async (host: string): Promise<number | null> => {
      if (!host || host === '127.0.0.1' || host === 'localhost') return null;
      const samples: number[] = [];
      for (let i = 0; i < 3; i++) {
        const t0 = performance.now();
        try {
          await fetch(`https://${host}/?ping=${i}`, {
            mode: 'no-cors',
            cache: 'no-store',
            signal: AbortSignal.timeout(3000),
          });
          samples.push(performance.now() - t0);
        } catch {
          return null;
        }
      }
      return Math.round(Math.min(...samples));
    };
    void (async () => {
      const ms: Record<string, number> = {};
      await Promise.all(
        regions.map(async (r) => {
          const v = await measure(r.gatewayHost);
          if (v !== null) ms[r.id] = v;
        }),
      );
      if (!alive) return;
      const best = Object.entries(ms).sort((a, b) => a[1] - b[1])[0]?.[0] ?? null;
      setState({ ms, best, pending: false });
    })();
    return () => {
      alive = false;
    };
  }, [key]);
  return state;
}

export function CreateDatabaseWizard({
  projectId,
  onCreated,
}: {
  projectId?: string;
  onCreated: () => void;
}) {
  const projects = useApi<{ projects: Project[] }>(projectId ? null : '/projects');
  const regions = useApi<{
    regions: {
      id: string;
      label: string;
      default: boolean;
      gatewayHost: string;
      distanceKm: number | null;
    }[];
    planned: { id: string; label: string }[];
    suggested: string | null;
    from: { city: string | null; region: string | null; country: string | null } | null;
  }>('/regions');
  const ping = useRegionPing(regions.data?.regions ?? []);
  // Best guess for "closest": measured latency from this browser when the gateways answer, else the
  // server's geolocation distance. Both are hints; the customer's app may run somewhere else.
  const recommended =
    ping.best ?? regions.data?.suggested ?? regions.data?.regions.find((r) => r.default)?.id ?? null;
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [pid, setPid] = useState(projectId ?? '');
  const [region, setRegion] = useState<string | null>(null);
  const [size, setSize] = useState<SizeHint>('small');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateDatabaseResponse | null>(null);
  const effectiveProject = projectId ?? pid ?? projects.data?.projects[0]?.id ?? '';
  const effectiveRegion = region ?? recommended ?? regions.data?.regions[0]?.id ?? '';

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const r = await api<CreateDatabaseResponse>('/databases', {
        method: 'POST',
        json: {
          projectId: effectiveProject || projects.data?.projects[0]?.id,
          name,
          region: effectiveRegion || undefined,
          sizeHint: size,
        },
      });
      setResult(r);
      setName('');
      setStep(1);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="wizard-steps">
        {(['Name', 'Location', 'Size'] as const).map((label, i) => (
          <span key={label} data-n={i + 1} className={step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}>
            {label}
          </span>
        ))}
      </div>
      {step === 1 && (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void (async () => {
              // First database on a fresh account: create a default project so nobody has to learn about
              // projects before they have a database.
              if (!projectId && !projects.data?.projects.length) {
                const r = await api<{ project: { id: string } }>('/projects', {
                  method: 'POST',
                  json: { name: 'default' },
                });
                setPid(r.project.id);
                await projects.reload();
              }
              setStep(2);
            })();
          }}
        >
          {!projectId && !!projects.data?.projects.length && (
            <select
              value={pid || projects.data?.projects[0]?.id || ''}
              onChange={(e) => setPid(e.target.value)}
              required
            >
              {projects.data?.projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <input
            placeholder="production"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={64}
          />
          <button disabled={!name.trim()} type="submit">
            Next
          </button>
        </form>
      )}
      {step === 2 && (
        <div>
          <div className="choice-grid">
            {regions.data?.regions.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`choice ${effectiveRegion === r.id ? 'active' : ''}`}
                onClick={() => setRegion(r.id)}
              >
                <strong>{r.label}</strong>
                <span className="badge active">available now</span>
                {recommended === r.id && <span className="badge">recommended</span>}
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {ping.ms[r.id] !== undefined
                    ? `≈ ${ping.ms[r.id]} ms from here`
                    : r.distanceKm !== null
                      ? `≈ ${r.distanceKm.toLocaleString()} km from you`
                      : ping.pending
                        ? 'measuring…'
                        : ''}
                </div>
              </button>
            ))}
            {regions.data?.planned.map((r) => (
              <button key={r.id} type="button" className="choice planned" disabled title="Coming soon">
                <strong>{r.label}</strong>
                <span className="badge">coming soon</span>
              </button>
            ))}
          </div>
          <p className="muted">
            {regions.data?.from?.city || regions.data?.from?.region
              ? `You seem to be near ${[regions.data.from.city, regions.data.from.region, regions.data.from.country].filter(Boolean).join(', ')}; the recommended location is the closest one. `
              : ''}
            Pick the location closest to where your <em>app</em> runs, which may not be where you are. You can
            move a database to any other location later, for free and without changing anything on your side:
            the connection string stays the same.
          </p>
          <div className="row">
            <button className="secondary" onClick={() => setStep(1)}>
              Back
            </button>
            <button onClick={() => setStep(3)} disabled={!effectiveRegion}>
              Next
            </button>
          </div>
        </div>
      )}
      {step === 3 && (
        <div>
          <div className="choice-grid">
            {SIZES.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`choice ${size === s.id ? 'active' : ''}`}
                onClick={() => setSize(s.id)}
              >
                <strong>{s.label}</strong>
                <div className="muted">{s.hint}</div>
              </button>
            ))}
          </div>
          <p className="muted">
            This only helps us plan capacity. You are billed for actual use and nothing is capped by this
            choice.
          </p>
          {error && <p className="error">{error}</p>}
          <div className="row">
            <button className="secondary" onClick={() => setStep(2)}>
              Back
            </button>
            <button disabled={busy} onClick={() => void create()}>
              {busy ? 'Creating…' : `Create "${name}"`}
            </button>
          </div>
        </div>
      )}
      {!projectId && projects.data?.projects.length === 0 && (
        <p className="muted">
          Create a <Link to="/projects">project</Link> first.
        </p>
      )}
      {result && (
        <Modal title={`${result.database.name} is ready`} onClose={() => setResult(null)}>
          <CredentialsReveal result={result} />
          <p style={{ marginTop: 14 }}>
            <Link to={`/databases/${result.database.id}`}>Open database</Link> ·{' '}
            <Link to={`/databases/${result.database.id}/data`}>Browse data</Link>
          </p>
        </Modal>
      )}
    </div>
  );
}

/** Kept for callers that used the old name. */
export const CreateDatabaseForm = CreateDatabaseWizard;

export function DatabasesPage() {
  const dbs = useApi<{ databases: Database[] }>('/databases');
  const forecast = useApi<Forecast>('/forecast');
  return (
    <div>
      <h1>Databases</h1>
      <div className="card">
        <h2>New database</h2>
        <CreateDatabaseWizard onCreated={() => void dbs.reload()} />
      </div>
      <div className="card">
        {dbs.data?.databases.length === 0 && <p className="muted">No databases yet.</p>}
        {dbs.data && dbs.data.databases.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Location</th>
                <th>Size</th>
                <th>Last activity</th>
                <th>This month</th>
                <th>Projected</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dbs.data.databases.map((d) => (
                <tr key={d.id}>
                  <td>
                    <Link to={`/databases/${d.id}`}>{d.name}</Link>
                  </td>
                  <td>
                    <StatusBadge state={d.state} />
                  </td>
                  <td className="muted">{d.region}</td>
                  <td>{formatBytes(d.sizeBytes)}</td>
                  <td>{formatDate(d.lastActivityAt)}</td>
                  <td>{formatMoney(d.monthToDateMicroUsd)}</td>
                  <td className="muted">
                    {forecast.data?.databases[d.id]
                      ? formatMoney(forecast.data.databases[d.id]!.projectedMonthMicro)
                      : '—'}
                  </td>
                  <td>
                    <Link to={`/databases/${d.id}/data`}>Data</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
